import React, { useEffect, useState, Fragment } from "react";
import { useDispatch } from "react-redux";
import { Formik, Form, Field } from "formik";
import QueryString from "query-string";

import Box from "@material-ui/core/Box";
import Container from "@material-ui/core/Container";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import Pagination from "@material-ui/lab/Pagination";

import pageTitle from "../../utils/pageTitle";
import history from "../../store/history";
import makeApiRequest from "../../utils/axios/new-axios";
import TextField from "../../components/formik/TextField";
import SelectPlain from "../../components/formik/SelectPlain";
import SubmitButton from "../../components/button/SubmitButton";
import { TRV_USER_SEARCH_URL, RECORDS_FETCH_LIMIT } from "../../config";
import { showErrorAlert } from "../../store/actions/alerts";
import ComponentLoader from "../../components/loader/ComponentLoader";

import ResultGrid from "../../components/travalours/ResultGrid";

const useStyles = makeStyles((theme) => ({
  paper: {
    width: "100%",
    padding: "10px",
  },
  form: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    alignItems: "center",
  },
  textField: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    paddingBottom: "13px",
    minWidth: "300px",
  },
  select: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    paddingBottom: "13px",
    minWidth: "130px",
  },
}));

export default function SearchTravalour({ location }) {
  pageTitle("Search Travalours");
  const dispatch = useDispatch();
  const classes = useStyles();
  const [result, setResult] = useState(null);
  const [noResult, setNoResult] = useState(false);
  const [isLoading, setIsloading] = useState(false);
  const [initialValues, setInitialValues] = useState({
    name: "",
    status: -1,
    sortBy: 1,
  });
  const pathName = location.pathname;

  useEffect(() => {
    const query = QueryString.parse(location.search);
    if (!!query) {
      const name = !!query.name ? query.name.trim() : null;
      const status =
        query.status && Number(query.status) >= 0 ? Number(query.status) : null;

      const page = Number(query.page) || null;
      const limit = Number(query.limit) || RECORDS_FETCH_LIMIT;

      const sort = !!query.sort ? query.sort.trim().toLowerCase() : null;
      const order = !!query.order ? query.order.trim().toLowerCase() : null;
      const sortBy =
        sort === "name"
          ? order === "desc"
            ? 2
            : 1
          : sort === "date"
            ? order === "desc"
              ? 3
              : 4
            : 1;

      setInitialValues({
        name: name || "",
        status: status !== null ? status : -1,
        sortBy,
      });

      if (!!name || status !== null) {
        setIsloading(true);
        makeApiRequest({
          url: TRV_USER_SEARCH_URL.url,
          method: TRV_USER_SEARCH_URL.method,
          params: {
            name,
            status,
            sort,
            order,
            page,
            limit,
          },
        })
          .then((response) => {
            if (response.status === 200) {
              setNoResult(false);
              setResult(response.data);
            } else {
              setNoResult(true);
              setResult(null);
            }
            setIsloading(false);
          })
          .catch((error) => {
            setIsloading(false);
            dispatch(
              showErrorAlert(
                "Error : " +
                (!!error.response
                  ? `${error.response.status}! - ${error.response.data.errorMessage}`
                  : error.message)
              )
            );
          });
      }
    }
    // eslint-disable-next-line
  }, []);

  const handlePagination = (e, page) => {
    const query = QueryString.parse(location.search);
    history.push(
      `${pathName}?${QueryString.stringify({
        ...query,
        page,
      })}`
    );
  };

  return (
    <Container style={{ padding: "10px" }}>
      <h3>Search Travalours</h3>
      <Paper className={classes.paper}>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={(values) => {
            let sort = "name";
            let order = "asc";
            switch (values.sortBy) {
              case 1: //no changes
                break;
              case 2:
                order = "desc";
                break;
              case 3:
                sort = "date";
                order = "desc";
                break;
              case 4:
                sort = "date";
                break;
              default:
                //no changes
                break;
            }

            const params = {
              name: values.name.trim().length > 0 ? values.name.trim() : null,
              status: values.status >= 0 ? values.status : null,
              sort,
              order,
              page: 1,
              limit: RECORDS_FETCH_LIMIT,
            };

            //remove the null keys
            Object.keys(params).forEach(
              (key) => params[key] == null && delete params[key]
            );

            history.push(`${pathName}?${QueryString.stringify(params)}`);
          }}
        >
          {(props) => {
            const { touched, errors } = props;
            return (
              <Form className={classes.form}>
                <Field
                  component={TextField}
                  name="name"
                  margin="dense"
                  label="Name or Username"
                  className={classes.textField}
                  helperText={touched.name && errors.name && errors.name}
                  error={!!touched.name && !!errors.name}
                  disabled={isLoading}
                  autoFocus
                />
                <Field
                  component={SelectPlain}
                  name="status"
                  label="Status"
                  disabled={isLoading}
                  className={classes.select}
                  options={[
                    {
                      value: -1,
                      label: "All",
                    },
                    {
                      value: 1,
                      label: "Active",
                    },
                    {
                      value: 2,
                      label: "Suspended",
                    },
                  ]}
                />
                <Field
                  component={SelectPlain}
                  name="sortBy"
                  label="Sort By"
                  disabled={isLoading}
                  className={classes.select}
                  options={[
                    {
                      value: 1,
                      label: "Name: Ascending",
                    },
                    {
                      value: 2,
                      label: "Name: Descending",
                    },
                    {
                      value: 3,
                      label: "Date: Newest",
                    },
                    {
                      value: 4,
                      label: "Date: Oldest",
                    },
                  ]}
                />
                <SubmitButton disabled={isLoading}>Search</SubmitButton>
              </Form>
            );
          }}
        </Formik>
      </Paper>
      <ComponentLoader isLoading={isLoading} />
      {!isLoading && !noResult && result && result.users && (
        <Fragment>
          <Typography
            variant="caption"
            color="textSecondary"
            style={{ margin: "15px" }}
            display="block"
            gutterBottom
          >
            {result.stats &&
              `Total Result(s) : ${result.stats.records.total}, Fetched : ${result.stats.records.fetched}, Page : ${result.stats.pages.current}`}
          </Typography>
          {result.users.map((user) => {
            return <ResultGrid user={user} key={user.id} />;
          })}
          <Box
            justifyContent="center"
            display="flex"
            style={{ margin: "20px" }}
          >
            <Pagination
              count={result.stats.pages.total}
              page={result.stats.pages.current}
              boundaryCount={3}
              color="primary"
              shape="rounded"
              variant="outlined"
              onChange={handlePagination}
            />
          </Box>
        </Fragment>
      )}
      {!isLoading && noResult && (
        <Box
          justifyContent="center"
          display="flex"
          style={{ marginTop: "30px" }}
        >
          No Result Found !!
        </Box>
      )}
    </Container>
  );
}
